import { Menu, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/dashboard/flows': 'Flow Configurator',
  '/dashboard/marketing': 'Marketing',
  '/dashboard/billing': 'Billing & Subscriptions',
  '/dashboard/profile': 'Profile & Settings',
};

export function Topbar({ onMenuToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || 'Dashboard';

  const initials = user?.full_name
    ? user.full_name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  return (
    <header
      className="h-16 bg-white border-b border-gray-100 flex items-center px-4 sm:px-6 gap-4 shrink-0 sticky top-0 z-30"
      data-testid="dashboard-topbar"
    >
      {/* Mobile hamburger */}
      <button
        onClick={onMenuToggle}
        className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="topbar-menu-btn"
        aria-label="Toggle menu"
      >
        <Menu className="w-5 h-5 text-slate-600" />
      </button>

      {/* Page title */}
      <h1
        className="flex-1 text-[15px] font-semibold text-slate-900"
        data-testid="topbar-title"
      >
        {title}
      </h1>

      {/* User dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors"
            data-testid="user-menu-trigger"
          >
            <div className="w-8 h-8 rounded-full bg-[#00D1B2]/15 flex items-center justify-center text-[#00D1B2] font-bold text-xs shrink-0">
              {initials}
            </div>
            <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[130px] truncate">
              {user?.full_name}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-3 py-2">
            <p className="text-xs font-medium text-slate-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate mt-0.5">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={logout}
            className="text-red-600 cursor-pointer gap-2 focus:text-red-600 focus:bg-red-50"
            data-testid="topbar-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
